import awardIcon from '@/assets/icons/icon_award.png'
import shieldIcon from '@/assets/icons/icon_shield.png'
import temperatureIcon from '@/assets/icons/icon_temperature.png'
import solutionImageOne from '@/assets/images/our_solutions_01.png'
import solutionImageTwo from '@/assets/images/our_solutions_02.png'
import solutionImageThree from '@/assets/images/our_solutions_03.png'
import workImageOne from '@/assets/images/our_work_01.png'
import workImageTwo from '@/assets/images/our_work_02.png'
import Button from '@/shared/components/Button'
import OffsetFrame from '@/shared/components/OffsetFrame'

import './HomePage.css'

import FeatureItem from '../components/FeatureItem'
import HomeHeader from '../components/HomeHeader'
import HomeHero from '../components/HomeHero'
import ImageLinkCard from '../components/ImageLinkCard'
import SectionHeading from '../components/SectionHeading'

const heroBenefits = [
  ['10 Years Guarantee', 'Our commitment to quality, durability, and your peace of mind.'],
  ['Cooler Buildings', 'Insulation systems that keep your space more comfortable.'],
  ['Energy Efficiency', 'Insulated systems that reduce heat transfer.'],
  ['Stronger Protection', 'Engineered for Caribbean weather conditions.'],
  ['Built for the Caribbean', 'Products made for local conditions.'],
]

const solutions = [
  { label: 'Tecma Flat Roof System', alt: 'Tecma flat roof system building', variant: 'flat-roof' as const, imageSrc: solutionImageOne },
  { label: 'Insulated Panels', alt: 'Insulated panel commercial roof', variant: 'panels' as const, imageSrc: solutionImageTwo },
  { label: 'Metal Materials', alt: 'Modern home using metal materials', variant: 'metal' as const, imageSrc: solutionImageThree },
]

const projects = [
  { label: 'Home Projects', alt: 'Residential Tecma project', href: '#home-projects', imageSrc: workImageOne },
  { label: 'Commercial Projects', alt: 'Commercial Tecma project', href: '#commercial-projects', imageSrc: workImageTwo },
]

function HeroBenefits() {
  return (
    <section aria-label="Tecma benefits" className="relative z-10 bg-[#131a54] text-white">
      <div className="mx-auto grid max-w-6xl divide-y divide-white/25 px-5 py-5 sm:grid-cols-5 sm:divide-x sm:divide-y-0 min-[900px]:py-3 lg:px-8">
        {heroBenefits.map(([title, description]) => (
          <div key={title} className="px-3 py-2 first:pl-0 sm:first:pl-0">
            <p className="text-xs font-bold leading-tight">{title}</p>
            <p className="mt-1 text-[9px] leading-tight text-[#e9ebf6]/85">{description}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

function SolutionsSection() {
  return (
    <section id="solutions" className="bg-[#e9ebf6] px-5 py-16 sm:max-[899px]:py-20 min-[900px]:px-0 min-[900px]:py-0">
      <div className="mx-auto max-w-6xl min-[900px]:max-w-[840px] min-[900px]:pb-[36px] min-[900px]:pt-[26px]">
        <SectionHeading eyebrow="Our Solutions" title="Our Roofing Solutions" />
        <div className="mt-8 grid gap-5 md:grid-cols-3 min-[900px]:mt-6 min-[900px]:gap-[53px]">
          {solutions.map((solution) => (
            <ImageLinkCard key={solution.label} {...solution} href="#products" compact darkLabel />
          ))}
        </div>
        <div className="mt-10 text-center min-[900px]:mt-[18px]">
          <Button size="section" aria-label="View all products">View All Products</Button>
        </div>
      </div>
    </section>
  )
}

function ProjectsSection() {
  return (
    <section className="relative overflow-hidden bg-[#131a54] px-5 py-16 min-[900px]:px-0 min-[900px]:py-0 lg:px-8">
      <div aria-hidden="true" className="absolute -bottom-14 -left-12 h-36 w-36 rotate-45 border-4 border-[#b74736]" />
      <div aria-hidden="true" className="absolute -bottom-10 -left-18 h-36 w-36 rotate-45 border-4 border-[#e9ebf6]" />
      <div className="relative mx-auto max-w-6xl min-[900px]:max-w-[840px] min-[900px]:pb-[48px] min-[900px]:pt-[26px]">
        <div className="flex flex-col gap-7 sm:flex-row sm:items-end sm:justify-between min-[900px]:!items-start">
          <SectionHeading eyebrow="Our Work" title="Recent Projects" inverse titleClassName="min-[900px]:mt-[14px]" />
          <Button size="sectionInverse" className="min-[900px]:mr-[35px] min-[900px]:mt-[7px]" aria-label="View all projects">View all Projects</Button>
        </div>
        <div className="mt-8 grid gap-8 md:grid-cols-2 min-[900px]:-mt-[2px] min-[900px]:gap-[34px]">
          {projects.map((project) => (
            <ImageLinkCard key={project.label} {...project} variant="project" wide />
          ))}
        </div>
      </div>
    </section>
  )
}

function BenefitsBand() {
  return (
    <section aria-label="More Tecma benefits" className="bg-white px-5 py-8 min-[900px]:h-[121px] min-[900px]:px-0 min-[900px]:py-0 lg:px-8">
      <div className="mx-auto grid max-w-6xl gap-6 divide-y divide-[#131a54]/70 sm:grid-cols-3 sm:divide-x sm:divide-y-0 min-[900px]:h-full min-[900px]:max-w-[796px] min-[900px]:!grid-cols-[240px_313px_243px] min-[900px]:items-start min-[900px]:gap-0 min-[900px]:!divide-x-0 min-[900px]:pt-[19px]">
        <div className="pt-5 sm:px-6 sm:pt-0 first:pl-0 min-[900px]:!pr-[20px]">
          <FeatureItem icon={<img src={temperatureIcon} alt="" className="h-[48px] w-auto" />} title="Cooler Buildings" description="Insulation system that helps keep your space more comfortable and energy efficient." />
        </div>
        <div className="pt-5 sm:px-6 sm:pt-0 min-[900px]:border-l min-[900px]:border-[#131a54] min-[900px]:!pl-[54px] min-[900px]:!pr-[20px]">
          <FeatureItem icon={<img src={shieldIcon} alt="" className="h-[48px] w-auto" />} title="Stronger Protection" description="System engineered to stand better against Caribbean weather." />
        </div>
        <div className="pt-5 sm:px-6 sm:pt-0 last:pr-0 min-[900px]:border-l min-[900px]:border-[#131a54] min-[900px]:!pl-[59px]">
          <FeatureItem icon={<img src={awardIcon} alt="" className="h-[48px] w-auto" />} title="10 Years Guarantee" description="Our commitment to quality, durability, and your peace of mind." />
        </div>
      </div>
    </section>
  )
}

function QuoteSection() {
  return (
    <section id="contact" className="bg-[#e9ebf6] px-5 py-20 min-[900px]:h-[382px] min-[900px]:px-0 min-[900px]:py-0 lg:px-8">
      <div className="mx-auto grid max-w-6xl gap-16 min-[900px]:max-w-[814px] min-[900px]:grid-cols-[386px_1fr] min-[900px]:gap-[71px] min-[900px]:pt-[30px] lg:grid-cols-2 lg:items-end">
        <OffsetFrame variant="quote" className="p-6 sm:p-8 min-[900px]:h-[266px] min-[900px]:!p-[18px] min-[900px]:!pt-[27px]">
          <p className="flex items-center gap-2 text-base font-extrabold uppercase">Let&apos;s Get Started<span aria-hidden="true" className="h-px w-6 bg-[#b74736]" /></p>
          <h2 className="mt-5 text-5xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl min-[900px]:mt-[18px] min-[900px]:!text-[48px]">Get Your Free<br />Quotation</h2>
          <p className="mt-7 max-w-md text-xl leading-tight min-[900px]:mt-[40px] min-[900px]:w-[calc(100%_-_36px)] min-[900px]:max-w-none min-[900px]:!text-[15px]">Tell us about your project and we&apos;ll provide a detailed, no-obligation quotation</p>
        </OffsetFrame>
        <div className="pb-3 min-[900px]:pt-[168px]">
          <p className="max-w-md text-lg leading-tight min-[900px]:text-[18px]">Tell us about your project and we&apos;ll provide a detailed, no-obligation quotation</p>
          <div className="mt-8 min-[900px]:mt-[18px]">
            <Button size="quote" aria-label="Request your quote">Request Your Quote</Button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default function HomePage() {
  return (
    <div className="home-page">
      <div className="relative">
        <HomeHeader />
        <HomeHero />
      </div>
      <HeroBenefits />
      <SolutionsSection />
      <ProjectsSection />
      <BenefitsBand />
      <QuoteSection />
    </div>
  )
}
